import { lineString } from "@turf/helpers";
import { transformScale } from "@turf/turf";
import { PDFDocument, PDFDict, PDFName, PDFArray, PDFNumber, PDFObject } from "pdf-lib";
import { pdfjs } from "react-pdf";


export async function attemptGetGPTSArrayBuffer(doc: ArrayBuffer) {
   const textDecoder = new TextDecoder("utf-8");
   const pdfText = textDecoder.decode(doc);
   return attemptGetGPTSRaw(pdfText);
}

export async function pdfToImage(doc: ArrayBuffer): Promise<Blob> {
   const pdf = await pdfjs.getDocument(doc).promise
   const page = await pdf.getPage(1);
   const canvas = document.createElement('canvas')
   const viewport = page.getViewport({ scale: 2 });
   canvas.width = viewport.width;
   canvas.height = viewport.height;
   return new Promise((resolve, reject) => {
      page.render({
         canvas: canvas,
         viewport: viewport
      }).promise.then(() => {
         canvas.toBlob((blob) => { if (blob) { resolve(blob) } else { reject("Failed") } }, "image/png");
      }).catch((error) => {
         console.error("Error rendering PDF page:", error);
         reject(error);
      });
   });
}

export async function attemptGetGPTSPDF(doc: PDFDocument) {
   function handlePDFObject(object: PDFObject) {
      switch (object.constructor) {
         case PDFDict:
            const dict = object as PDFDict;
            return dict.entries().map((a) => {
               return a[0].asString() + " " + handlePDFObject(a[1]);
            })
         case PDFArray:
            const arr = object as PDFArray;
            return "[" + arr.asArray().map((item) => handlePDFObject(item)).join(" ") + "]";
         case PDFNumber:
            const num = object as PDFNumber;
            return num.asNumber().toString();
         case PDFName:
            const name = object as PDFName;
            return name.asString();
         default:
            return object.toString();
      }
   }
   let pdfText = "";
   doc.context.enumerateIndirectObjects().forEach(async ([_ref, object]) => {
      pdfText += handlePDFObject(object) + "\n";
   });
   return attemptGetGPTSRaw(pdfText);
}

export async function attemptGetGPTSRaw(pdfText: string) {
   // const textDecoder = new TextDecoder("utf-8");
   // const pdfText = textDecoder.decode(doc);
   const gptsRegex = /\/GPTS\s*\[\s*([-\d.\s]+)\s*\]/g;
   const lptsRegex = /\/LPTS\s*\[\s*([-\d.\s]+)\s*\]/g;
   const bboxRegex = /\/BBox\s*\[\s*([-\d.\s]+)\s*\]/g;
   const boundsRegex = /\/Bounds\s*\[\s*([-\d.\s]+)\s*\]/g;

   const gptsMatchArr = pdfText.matchAll(gptsRegex);
   const lptsMatchArr = pdfText.matchAll(lptsRegex);
   const bboxMatchArr = pdfText.matchAll(bboxRegex);
   const boundsMatchArr = pdfText.matchAll(boundsRegex);

   let gptsMatch = gptsMatchArr.next().value
   let lptsMatch = lptsMatchArr.next().value
   let bboxMatch = bboxMatchArr.next().value
   let boundsMatch = boundsMatchArr.next().value

   while (gptsMatch && !findMatchInDenmark(parseCoords(gptsMatch))) {
      console.log("Skipping GPTS Match:", parseCoords(gptsMatch));
      gptsMatch = gptsMatchArr.next().value;
      lptsMatch = lptsMatchArr.next().value
      bboxMatch = bboxMatchArr.next().value
      boundsMatch = boundsMatchArr.next().value
   }

   function parseCoords(match?: RegExpExecArray | null) {
      if (match && match[1]) {
         const coordsArray = match[1].trim().split(/\s+/).map(numStr => parseFloat(numStr));
         const coords2 = coordsArray.reduce((resultArray: number[][], item, index) => {
            const idx = index % 2;
            if (idx === 0) {
               resultArray.push([item]);
            } else {
               resultArray[resultArray.length - 1].push(item);
            }
            return resultArray;
         }, []);
         return coords2;
      }
      return undefined;
   }

   console.log("GPTS Match:", parseCoords(gptsMatch));
   console.log("LPTS Match:", parseCoords(lptsMatch));
   console.log("BBox Match:", parseCoords(bboxMatch));
   console.log("Bounds Match:", parseCoords(boundsMatch));



   return transformGPTS(rotateCoords(parseCoords(gptsMatch), parseCoords(boundsMatch), parseCoords(lptsMatch)));
}

function findMatchInDenmark(coords?: number[][]) {
   if (!coords) return false;
   let denmarkBbox = [[7.741269070919969, 57.808475243053806], [13.511745485770007, 54.47934487366999]];
   let within = coords.every(([y, x]) => x >= denmarkBbox[0][0] && x <= denmarkBbox[1][0] && y <= denmarkBbox[0][1] && y >= denmarkBbox[1][1]);
   return within;
}

function rotateCoords(coords: number[][] | undefined, bounds: number[][] | undefined, lpts: number[][] | undefined = undefined) {
   if (!coords || !bounds) return coords;
   let flipIndex = bounds.findIndex(([x, y]) => x - y == 1);
   if (bounds[flipIndex + 1][0] === 1 || bounds[flipIndex - 1][0] == 0) {
      coords = coords.toReversed();
      bounds = bounds.slice(0, 4).toReversed();
      lpts = lpts?.toReversed();
   }

   let times = bounds.findIndex(([x, y]) => x - y == -1);
   console.log(times);
   while (times > 0) {
      coords = [...coords.slice(1), coords[0]];
      times--;
   } while (times < 0) {
      coords = [...coords.slice(-1), ...coords.slice(0, -1)];
      times++;
   }


   let scale = lpts?.reduce((pre, curr) => {
      return { max: Math.max(pre.max, curr[0]), min: Math.min(pre.min, curr[0]) };
   }, { max: 0, min: Infinity }) ?? { max: 1, min: 0 };
   coords = transformScale(lineString(coords), 1 / (scale.max - scale.min)).geometry.coordinates as number[][];

   return coords;
}

function transformGPTS(gpts?: number[][]) {
   return gpts?.map(([x, y]) => [y, x]);
}

export async function attemptGETGPTS(doc: PDFDocument) {
   return null;
   // Overvej om det er lettere bare at finde GPTS med regex i raw PDF data.


   let coords: number[] = [];
   doc.context.enumerateIndirectObjects().forEach(async ([_ref, object]) => {
      if (object instanceof PDFDict) {
         object.entries().forEach(([key, value]) => {
            if (key instanceof PDFName && key.asString() === "/GPTS") {
               if (value instanceof PDFArray) {
                  value.asArray().map((item) => {
                     if (item instanceof PDFNumber) {
                        coords.push(item.asNumber());
                     }
                  });
               }
            }
         });
      };
   });
   if (coords.length) {
      const coords2 = coords.reduce((resultArray: number[][], item, index) => {
         const idx = index % 2;
         if (idx === 0) {
            resultArray.push([item]);
         } else {
            resultArray[resultArray.length - 1].unshift(item);
         }
         return resultArray;
      }, [])
      coords2.reverse();

      const first = coords2.pop()
      return [first, ...coords2];
   }
   return null;
}